import { ClientOrganization, OrganizationBilling } from '@graphql';
import { FormikProps } from 'formik';
import { ConfirmationModal } from '@components';
import SyncAlt from '@mui/icons-material/SyncAlt';
import { COLORS } from '@styles';
import { useMemo } from 'react';
import { BillingModelLabels, ChangePlanType, IManagePlansAndSeatsFormikValues } from '../types';

interface IChangePlanConfirmationModalProps {
  isOpen: boolean;
  organization?: ClientOrganization;
  organizationBilling?: OrganizationBilling;
  formik: FormikProps<IManagePlansAndSeatsFormikValues>;
  onClose: () => void;
  getTierNameById: (tierId: string) => string;
  onFormSubmit: () => Promise<void>;
}

function ChangePlanConfirmationModal({
  isOpen,
  organization,
  formik,
  organizationBilling,
  onClose,
  getTierNameById,
  onFormSubmit,
}: IChangePlanConfirmationModalProps) {
  const changeType: ChangePlanType = useMemo(() => {
    if (formik.values.billingModel !== organizationBilling?.billingModel) {
      return ChangePlanType.BILLING_MODEL_CHANGE;
    }
    return ChangePlanType.TIER_CHANGE;
  }, [formik.values.billingModel, organizationBilling?.billingModel]);

  const title = useMemo(() => {
    switch (changeType) {
      case ChangePlanType.BILLING_MODEL_CHANGE:
        return `Are you sure you want to change ${organization?.name} from ${
          organizationBilling?.billingModel !== undefined
            ? BillingModelLabels[organizationBilling.billingModel as keyof typeof BillingModelLabels]
            : ''
        } to ${
          formik.values.billingModel !== undefined
            ? BillingModelLabels[formik.values.billingModel as keyof typeof BillingModelLabels]
            : ''
        } as the payment method at ElektraFi?`;
      case ChangePlanType.TIER_CHANGE:
      default:
        return `Are you sure you want to change ${organization?.name} from ${getTierNameById(
          organization?.currentTier?.id || '',
        )} to ${getTierNameById(formik.values.currentTierId)} at ElektraFi?`;
    }
  }, [
    changeType,
    formik.values.billingModel,
    formik.values.currentTierId,
    getTierNameById,
    organization?.currentTier?.id,
    organization?.name,
    organizationBilling?.billingModel,
  ]);

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      variant="warning"
      title={title}
      confirmationText={organization?.name || ''}
      icon={<SyncAlt sx={{ color: COLORS.NEUTRAL_DARK_2 }} />}
      confirmationButtonLabel="Confirm"
      onConfirm={onFormSubmit}
    >
      <p>
        This change will affect billing, payment method, number of seats, access to some add-on
        features by all employees and limit the management tools available to this organization. To
        proceed, please type <strong>{organization?.name}</strong>
      </p>
    </ConfirmationModal>
  );
}

export default ChangePlanConfirmationModal;
